import { useState } from "react";
import { Link } from "react-router-dom";
import { X, Lock, MapPin, Heart, BadgeCheck, Flag, EyeOff } from "lucide-react";
import { CATEGORIES } from "@/lib/categories";
import { getUser } from "@/lib/user";
import { ReportModal } from "@/components/ReportModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  free_view: "Vue gratuite utilisée",
  already_viewed: "Déjà consulté — aucune vue décomptée",
  subscribed: "Accès abonné",
  self: "Votre profil",
};

export const FicheProfilModal = ({ fiche, onClose }) => {
  const [showReport, setShowReport] = useState(false);
  if (!fiche) return null;
  const { profile, access } = fiche;
  const cat = CATEGORIES[profile.category];
  const user = getUser();
  const photoSrc = profile.photo
    ? `${BACKEND}${profile.photo}${user ? `?viewer_id=${user.id}` : ""}`
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fiche-profil-modal">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="relative h-72" style={{ backgroundColor: cat?.colorSoft }}>
          {photoSrc ? (
            <img src={photoSrc} alt={profile.firstname} className="w-full h-full object-cover object-top" />
          ) : (
            <div className="h-full flex items-center justify-center font-heading text-7xl font-extrabold text-stone-300">
              {profile.firstname.charAt(0)}
            </div>
          )}
          <button
            onClick={onClose}
            data-testid="fiche-close"
            className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-colors"
          >
            <X size={17} />
          </button>
          <span
            className="absolute bottom-3 left-4 text-[11px] font-bold tracking-wider uppercase px-3 py-1.5 rounded-full text-white"
            style={{ backgroundColor: cat?.color }}
          >
            {cat?.name}
          </span>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#0A5C36]" data-testid="fiche-access-reason">
            <BadgeCheck size={14} /> {REASON_LABELS[access?.reason] || "Accès autorisé"}
          </div>
          {profile.blind_date && user?.id !== profile.id && (
            <div className="flex items-start gap-2 text-xs text-[#9f2558] bg-[#9f2558]/10 rounded-xl px-3 py-2.5" data-testid="fiche-blind-date-info">
              <EyeOff size={14} className="shrink-0 mt-0.5" />
              <span><b>Mode Blind Date :</b> la photo se révélera après quelques échanges.</span>
            </div>
          )}
          <div className="flex items-baseline justify-between">
            <h3 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2">
              {profile.firstname}{profile.age ? `, ${profile.age}` : ""}
              {profile.verified && <BadgeCheck size={20} className="text-[#1D9BF0]" data-testid="fiche-badge-verifie" />}
            </h3>
            <span className="flex items-center gap-1 text-sm text-stone-500">
              <MapPin size={13} /> {profile.city}{profile.quartier ? ` · ${profile.quartier}` : ""}
            </span>
          </div>
          <p className="text-sm text-stone-600 leading-relaxed">{profile.description}</p>
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span className="flex items-center gap-2">
              <Heart size={15} className="text-[#E05638]" fill="#E05638" /> {profile.likes} like{profile.likes > 1 ? "s" : ""}
            </span>
            {user && user.id !== profile.id && (
              <button
                onClick={() => setShowReport(true)}
                data-testid="fiche-signaler-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-400 hover:text-[#E05638] transition-colors"
              >
                <Flag size={13} /> Signaler
              </button>
            )}
          </div>
        </div>
      </div>
      {showReport && <ReportModal autre={profile} userId={user.id} onClose={() => setShowReport(false)} />}
    </div>
  );
};

export const SubscriptionRequiredModal = ({ message, onClose, categoryId }) => {
  if (!message) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subscription-required-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-8 text-center space-y-5">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#E05638]/10 flex items-center justify-center">
          <Lock size={24} className="text-[#E05638]" />
        </div>
        <h3 className="font-heading text-xl font-extrabold text-stone-900">Vues gratuites épuisées</h3>
        <p className="text-sm text-stone-600 leading-relaxed" data-testid="subscription-required-message">{message}</p>
        <div className="flex flex-col gap-3">
          <Link
            to={`/abonnement?cat=${categoryId}`}
            data-testid="subscription-required-cta"
            className="w-full bg-[#E05638] hover:bg-[#C84428] text-white font-semibold py-3.5 rounded-full transition-colors"
          >
            Voir les abonnements
          </Link>
          <button onClick={onClose} data-testid="subscription-required-close" className="text-sm text-stone-500 hover:text-stone-800 transition-colors">
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};
